/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Fragment, type RefObject } from 'preact';
import type { CollectionData } from '#PRIVATE_TableCommon/Common.types';
import { useCallback } from 'preact/hooks';
import { LoadMoreCollection } from '../PRIVATE_LoadMoreCollection';
import { TableDisplayColumn } from './TableDisplayBase';
import { ViewportInfo } from '../PRIVATE_Table/Table';
import { TableSkeletonRow } from '../PRIVATE_Table/TableSkeletonRow';
import { MemoizeTableDisplayRow } from './TableDisplayRow';
import { ItemContext } from '../UNSAFE_Collection';
import { TableRowContext } from '.';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { backgroundStyles } from '../PRIVATE_Table/themes/Table.css';
import { TableDisplayNoData } from './TableDisplayNoData';
import { ComponentChildren } from 'preact';

/**
 * Props for the TableDisplayBody Component
 */
export type TableDisplayBodyProps<K extends string | number, D, C extends string> = {
  rootRef: RefObject<HTMLDivElement>;
  viewportInfo: ViewportInfo;
  tableId: string;
  isValidRender: boolean;
  getAccessibleRowHeaders: (context: TableRowContext<K, D>) => Set<C>;
  tableData: CollectionData<K, D>[];
  columnsArray: TableDisplayColumn<K, D, C>[];
  hasVerticalGridlines: boolean;
  hasHorizontalGridlines: boolean;
  hasFooters: boolean;
  hasVerticalUnderflow: boolean;
  hasMore: boolean;
  isRtl: boolean;
  isHighContrast: boolean;
  onLoadMore: () => void;
  noDataRenderer?: () => ComponentChildren;
  startStickyEdge?: number;
  endStickyEdge?: number;
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
  columnBorderEdges: { hasStart: boolean; hasEnd: boolean; width: number }[];
};

/**
 * The internal component used to render a the tbody of a Table.
 */
export function TableDisplayBody<K extends string | number, D, C extends string>({
  rootRef,
  viewportInfo,
  tableId,
  isValidRender,
  getAccessibleRowHeaders,
  tableData,
  columnsArray,
  hasVerticalGridlines,
  hasHorizontalGridlines,
  hasFooters,
  hasVerticalUnderflow,
  hasMore,
  onLoadMore,
  noDataRenderer,
  startStickyEdge,
  endStickyEdge,
  firstStickyColumnIndex,
  finalStickyColumnIndex,
  columnBorderEdges,
  isRtl,
  isHighContrast
}: TableDisplayBodyProps<K, D, C>) {
  const browser = getClientHints().browser;

  // returns a TableRowContext based on ItemContext (added key)
  const getRowContext = useCallback((context: ItemContext<CollectionData<K, D>>) => {
    return {
      index: context.index,
      data: context.data.data,
      key: context.data.metadata.key
    };
  }, []);

  const collectionChildrenFunc = (context: ItemContext<CollectionData<K, D>>) => {
    const tableRowContext = getRowContext(context);
    const rowKey = tableRowContext.key;
    const rowProps = {
      tableId: tableId,
      columnsArray: columnsArray,
      rowHeaderColumnKeys: getAccessibleRowHeaders(tableRowContext),
      hasVerticalGridlines: hasVerticalGridlines,
      hasHorizontalGridlines: hasHorizontalGridlines,
      hasFooters: hasFooters,
      hasVerticalUnderflow: hasVerticalUnderflow,
      hasMore: hasMore,
      rowIndex: tableRowContext.index,
      rowKey: rowKey,
      rowData: tableRowContext.data,
      isFinalRow: tableRowContext.index === tableData.length - 1,
      startStickyEdge: startStickyEdge,
      endStickyEdge: endStickyEdge,
      firstStickyColumnIndex: firstStickyColumnIndex,
      finalStickyColumnIndex: finalStickyColumnIndex,
      columnBorderEdges: columnBorderEdges,
      isRtl: isRtl,
      isHighContrast: isHighContrast
    };
    return <MemoizeTableDisplayRow {...rowProps} />;
  };

  const getNoDataProps = () => {
    return {
      rootRef: rootRef,
      tableId: tableId,
      columnsCount: columnsArray.length,
      ariaRowIndex: isValidRender ? 2 : 1,
      renderer: isValidRender ? noDataRenderer : undefined
    };
  };

  const isShowEmptyLoading = tableData.length === 0 && hasMore;
  const isShowNoData = tableData.length === 0 && !hasMore;

  return (
    <tbody class={backgroundStyles.inherit} role={'presentation'} data-oj-table-body={tableId}>
      {isShowNoData || !isValidRender ? (
        <TableDisplayNoData {...getNoDataProps()} />
      ) : (
        <Fragment>
          <LoadMoreCollection<CollectionData<K, D>>
            data={tableData}
            loadMoreIndicator={
              <TableSkeletonRow
                tableId={tableId}
                hasTabIndex={false}
                colspan={columnsArray.length}
                isLoadMore={!isShowEmptyLoading}
                isShowFocusRing={false}
                isEmbedded={false}
                isHighContrast={isHighContrast}
              />
            }
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            viewportConfig={viewportInfo.viewportConfig}>
            {collectionChildrenFunc}
          </LoadMoreCollection>
          <tr
            style={browser !== 'firefox' ? 'height: 100%;' : undefined}
            role={'presentation'}
            data-oj-table-spacer={tableId}
          />
        </Fragment>
      )}
    </tbody>
  );
}
